import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import type { z } from 'zod/v4';

import { ContactPersonFormSchema } from './use-create-contact-person';
import { type ApiResponse, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

const ContactPersonFormSchemaSnakeCase = ContactPersonFormSchema.transform(data => {
  const { address, ...rest } = data;

  return {
    ...rest,
    address: address ? { attributes: address } : null,
    phones: rest.phones?.map(phone => ({
      attributes: { number: phone.number },
      id: phone.id,
      _destroy: phone._destroy,
    })),
  };
});

export type UpdateContactPersonForm = z.infer<typeof ContactPersonFormSchema>;

type UpdateContactPersonVariables = {
  debtorId: string;
  contactPersonId: string;
  data: UpdateContactPersonForm;
};

export async function updateContactPersonFn(
  axiosInstance: Promise<AxiosInstance>,
  { debtorId, contactPersonId, data }: UpdateContactPersonVariables,
) {
  const instance = await axiosInstance;

  const snakeCaseData = ContactPersonFormSchemaSnakeCase.parse(data);

  return instance.put(`/debtors/${debtorId}/contact_persons/${contactPersonId}`, {
    id: contactPersonId,
    ...snakeCaseData,
  });
}

export function useUpdateContactPerson() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    UpdateContactPersonVariables
  >({
    mutationFn: variables => updateContactPersonFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contact-persons'] });
      client.invalidateQueries({ queryKey: ['contact-person'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    UpdateContactPersonVariables,
    'updateContactPerson'
  >('updateContactPerson', mutation);
}
